import React, { useState, useRef, useEffect } from 'react';
import './MoreActionsMenu.css';

const MoreActionsMenu = ({
  actions = {
    viewMore: true,
    fileSummary: true,
    download: true,
    rename: true,
    tryAgain: true,
    delete: true,
    reprocessAs: false,
  },
  onViewMore = () => {},
  onFileSummary = () => {},
  onDownload = () => {},
  onRename = () => {},
  onTryAgain = () => {},
  onDelete = () => {},
  onReprocessAs = () => {},
  onMenuOpen = () => {},
  position = 'bottom-left',
  triggerButton = null,
  closeOnClickOutside = true,
  closeOnAction = true,
  className = '',
  theme = 'light',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        closeOnClickOutside &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeOnClickOutside]);

  const handleAction = (callback) => {
    callback();
    if (closeOnAction) {
      setIsOpen(false);
    }
  };

  const hasEnabledActions = Object.values(actions).some(Boolean);

  if (!hasEnabledActions) {
    return null;
  }

  return (
    <div className={`more-actions-container ${className} ${theme}-theme ${disabled ? 'is-disabled' : ''}`}>
      <button
        ref={buttonRef}
        className="more-actions-trigger"
        onClick={() => {
          if (disabled) return;
          const opening = !isOpen;
          setIsOpen(opening);
          if (opening) {
            onMenuOpen();
          }
        }}
        aria-label="More actions"
        aria-expanded={isOpen}
        aria-haspopup="true"
        disabled={disabled}
      >
        {triggerButton || (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="2"/>
            <circle cx="12" cy="12" r="2"/>
            <circle cx="12" cy="19" r="2"/>
          </svg>
        )}
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className={`more-actions-menu ${position}`}
          role="menu"
        >
          <div className="more-actions-header">More Actions</div>
          <div className="more-actions-divider"></div>

          <div className="more-actions-list">
            {actions.viewMore && (
              <button
                className="more-actions-item"
                onClick={() => handleAction(onViewMore)}
                role="menuitem"
              >
                <svg className="action-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <span className="action-label">View More</span>
              </button>
            )}

            {actions.fileSummary && (
              <button
                className="more-actions-item"
                onClick={() => handleAction(onFileSummary)}
                role="menuitem"
              >
                <svg className="action-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" opacity="0.3"/>
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" fill="none" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="action-label">File Summary</span>
              </button>
            )}

            {actions.reprocessAs && (
              <button
                className="more-actions-item"
                onClick={() => handleAction(onReprocessAs)}
                role="menuitem"
              >
                <svg className="action-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                <span className="action-label">Reprocesar como</span>
              </button>
            )}

            {actions.download && (
              <button
                className="more-actions-item"
                onClick={() => handleAction(onDownload)}
                role="menuitem"
              >
                <svg className="action-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span className="action-label">Download</span>
              </button>
            )}

            {actions.rename && (
              <button
                className="more-actions-item"
                onClick={() => handleAction(onRename)}
                role="menuitem"
              >
                <svg className="action-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                <span className="action-label">Rename</span>
              </button>
            )}

            {actions.tryAgain && (
              <button
                className="more-actions-item"
                onClick={() => handleAction(onTryAgain)}
                role="menuitem"
              >
                <svg className="action-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                <span className="action-label">Reprocesar</span>
              </button>
            )}

            {actions.delete && (
              <button
                className="more-actions-item delete"
                onClick={() => handleAction(onDelete)}
                role="menuitem"
              >
                <svg className="action-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
                <span className="action-label">Delete</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MoreActionsMenu;
